import React, { useEffect, useMemo, useRef, useState } from 'react';
import { OverlayPanel } from 'primereact/overlaypanel';
import { Button } from 'primereact/button';
import { useHelpers } from './HelperService';

const HELPER_VISIBILITY_STORAGE_KEY = 'helperGlobalVisible';

const normalize = (value) => {
  return (value || '').toString().replace(/-/g, '_');
};

const getHashPathParts = () => {
  const hash = window.location.hash || '';
  const path = hash.replace('#', '');
  const pathname = path.split('?')[0];
  return pathname.split('/').filter(Boolean);
};

export const getCurrentRouteHelpContext = () => {
  const parts = getHashPathParts();
  const module = parts[0] || null;
  if (!module || module === 'help') {
    return null;
  }

  if (module === 'partner') {
    const action = parts[1] || 'list';
    if (action === 'show' || action === 'read') {
      return { module, screen: 'partner_read', section: null };
    }
    if (action === 'create' || action === 'update') {
      return { module, screen: 'partner_create_update', section: null };
    }
    return {
      module,
      screen: action === 'list' ? 'partner_list' : `partner_${normalize(action)}`,
      section: null,
    };
  }

  const entity = parts[1] || null;
  const action = parts[2] || null;

  if (!entity) {
    return {
      module,
      screen: 'dashboard',
      section: null,
    };
  }

  if (!action || action === 'list') {
    return {
      module,
      screen: `${normalize(entity)}_list`,
      section: null,
    };
  }

  if (action === 'create' || action === 'update') {
    return {
      module,
      screen: `${normalize(entity)}_create_update`,
      section: null,
    };
  }

  if (action === 'show' || action === 'read') {
    return {
      module,
      screen: `${normalize(entity)}_read`,
      section: null,
    };
  }
  return {
    module,
    screen: `${normalize(entity)}_${normalize(action)}`,
    section: null,
  };
};

const sortByPosition = (items) => {
  return [...(items || [])].sort((a, b) => {
    return Number(a.position || 0) - Number(b.position || 0);
  });
};

const filterHelpItems = ({ items, section, level, global }) => {
  let filtered = sortByPosition(items);

  if (section) {
    filtered = filtered.filter((item) => item.section === section);
  }

  if (level) {
    filtered = filtered.filter((item) => item.level === level);
  }

  if (!section && !level && global) {
    const pageItems = filtered.filter((item) => item.level === 'page');

    if (pageItems.length > 0) {
      return pageItems;
    }
  }

  return filtered;
};

const getHelpUrl = ({ module, module_code, screen, section } = {}) => {
  const moduleValue = module_code || module?.code || module;
  const moduleCode = typeof moduleValue === 'string' ? moduleValue : null;

  if (!moduleCode) {
    return '/#/help';
  }

  let url = `/#/help/${moduleCode}`;

  if (screen) {
    url += `/${screen}`;
  }

  if (section) {
    url += `/${section}`;
  }

  return url;
};

const buttonBaseStyle = {
  alignItems: 'center',
  background: '#f1f3f5',
  border: '1px solid #d7dce2',
  borderRadius: '50%',
  color: '#6c757d',
  cursor: 'pointer',
  display: 'inline-flex',
  fontSize: '12px',
  fontWeight: 700,
  height: '22px',
  justifyContent: 'center',
  lineHeight: 1,
  marginLeft: '6px',
  padding: 0,
  width: '22px',
};

const HelperComponent = ({
  module,
  screen,
  section = null,
  level = null,
  className = '',
  global = false,
  language,
  fallbackToFirst = true,
}) => {
  const overlayRef = useRef(null);
  const helperContext = useHelpers();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [routeContext, setRouteContext] = useState(getCurrentRouteHelpContext());
  const [globalVisible, setGlobalVisible] = useState(
    localStorage.getItem(HELPER_VISIBILITY_STORAGE_KEY) !== 'false'
  );

  const shouldWatchRoute = global || !module || !screen;

  useEffect(() => {
    if (!shouldWatchRoute) {
      return undefined;
    }

    const handleHashChange = () => {
      setRouteContext(getCurrentRouteHelpContext());
    };

    window.addEventListener('hashchange', handleHashChange);

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, [shouldWatchRoute]);

  useEffect(() => {
    if (!global) {
      return undefined;
    }

    const updateVisibility = () => {
      setGlobalVisible(localStorage.getItem(HELPER_VISIBILITY_STORAGE_KEY) !== 'false');
    };

    window.addEventListener('storage', updateVisibility);
    window.addEventListener('helperVisibilityChanged', updateVisibility);

    return () => {
      window.removeEventListener('storage', updateVisibility);
      window.removeEventListener('helperVisibilityChanged', updateVisibility);
    };
  }, [global]);

  const currentModule = module || routeContext?.module;
  const currentScreen = screen || routeContext?.screen;
  const currentSection = section || routeContext?.section;

  useEffect(() => {
    let cancelled = false;

    if (!helperContext || !currentModule || !currentScreen) {
      setItems([]);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    setItems([]);

    helperContext.loadHelpContents({
      module: currentModule,
      screen: currentScreen,
      language,
    }).then((result) => {
      if (cancelled) {
        return;
      }

      const filtered = filterHelpItems({
        items: result,
        section: currentSection,
        level,
        global,
      });

      if (filtered.length > 0 || !currentSection || !fallbackToFirst) {
        setItems(filtered);
        setLoading(false);
        return;
      }

      setItems(filterHelpItems({
        items: result,
        section: null,
        level: null,
        global,
      }));
      setLoading(false);
    }).catch(() => {
      if (!cancelled) {
        setItems([]);
        setLoading(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [helperContext, currentModule, currentScreen, currentSection, level, global, language, fallbackToFirst]);

  const helper = useMemo(() => {
    if (!items || items.length === 0) {
      return null;
    }

    return items[0];
  }, [items]);

  if (!localStorage.getItem('userToken') || (global && !globalVisible)) {
    return null;
  }

  if (!global && !helper) {
    return null;
  }

  const navigateToHelp = () => {
    overlayRef.current?.hide();
    window.location.href = getHelpUrl(helper || {
      module: currentModule,
      screen: currentScreen,
      section: currentSection,
    });
  };

  const hasContext = Boolean(currentModule && currentScreen);

  const hideGlobalHelper = () => {
    if (!window.confirm('Biztosan elrejti? A Segítség menüpontban később visszakapcsolhatja.')) {
      return;
    }

    overlayRef.current?.hide();
    localStorage.setItem(HELPER_VISIBILITY_STORAGE_KEY, 'false');
    window.dispatchEvent(new Event('helperVisibilityChanged'));
    setGlobalVisible(false);
  };

  return (
    <>
      <button
        type="button"
        className={`${global ? 'global-helper-button' : 'helper-component-button'} ${className}`.trim()}
        onClick={(event) => overlayRef.current.toggle(event)}
        style={global ? undefined : buttonBaseStyle}
        title="Segítség"
        aria-label="Segítség"
      >
        ?
      </button>

      <OverlayPanel ref={overlayRef} className="global-helper-overlay">
        <div className="global-helper-content">
          <div className="global-helper-title">
            {helper?.title || 'Segítség'}
          </div>

          {helper ? (
            <div
              className="global-helper-description"
              dangerouslySetInnerHTML={{
                __html: helper.body || '',
              }}
            />
          ) : (
            <div className="global-helper-description">
              {loading && <p>Betöltése folyamatban...</p>}
              {!loading && hasContext && (
                <p>Ehhez a felülethez jelenleg nincs külön segítség tartalom.</p>
              )}
            </div>
          )}

          <div className="global-helper-actions">
            <Button
              type="button"
              label={helper ? 'További információk' : 'Segítség megnyitása'}
              icon="pi pi-info-circle"
              className="p-button-sm"
              onClick={navigateToHelp}
            />
            {global && (
              <Button
                type="button"
                label="Segítség elrejtése"
                icon="pi pi-eye-slash"
                className="p-button-sm p-button-secondary p-button-text"
                onClick={hideGlobalHelper}
              />
            )}
          </div>
        </div>
      </OverlayPanel>
    </>
  );
};

export default HelperComponent;
